import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FlowDatasetsManagement from 'components/Flow/FlowDatasetsManagement';
import PermissionService from 'components/Common/PermissionService';
import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { DropdownMenu } from 'BaseComponents';
import { FLOW_STATUS } from 'utils/ignoramusEnums';
import { FlowRunContext } from 'pages/Flow/FlowRun/FlowRunProvider';
import { cleanRoom } from 'utils/spaUrls';
import { cleanRoomPermissions } from 'utils/appConstants';
import { useHistory, useParams } from 'react-router-dom';


const getTooltipContent = ({ runCount, jobStage }) => {
  const canCreateRuns = PermissionService.isAuthorized([
    cleanRoomPermissions.crFlowsBuilder,
    cleanRoomPermissions.crManageFlowRuns,
  ]);
  const canViewReports = PermissionService.isAuthorized([
    cleanRoomPermissions.crFlowsBuilder,
    cleanRoomPermissions.viewReports,
  ]);

  // Permission checks are ordered to handle combined scenarios.
  if (
    (canCreateRuns && jobStage === FLOW_STATUS.READY.key)
    || (canViewReports && runCount > 0)
  ) {
    return '';
  }

  if (!canCreateRuns && !canViewReports) {
    return 'You do not have permission to view or create runs. Contact your clean room administrator for access.';
  }

  if (canCreateRuns && jobStage !== FLOW_STATUS.READY.key) {
    return 'You can create runs but datasets have not been assigned.';
  }

  if (canViewReports && runCount === 0) {
    return 'You can view run reports but no run has been made.';
  }

  return '';
};
const getButtonLabel = ({
  runCount,
  jobStage = '',
}) => {
  if (jobStage !== FLOW_STATUS.READY.key && runCount > 0) {
    return 'Reports';
  }

  return 'Run & Reports';
};

const getMenuItems = ({
  crFlowID, runCount, openModal, history, crId, jobStage,
}) => {
  const itemsMenu = [];
  const canCreateRuns = PermissionService.isAuthorized([
    cleanRoomPermissions.crFlowsBuilder,
    cleanRoomPermissions.crManageFlowRuns,
  ]);
  const canViewReports = PermissionService.isAuthorized([
    cleanRoomPermissions.crFlowsBuilder,
    cleanRoomPermissions.viewReports,
    cleanRoomPermissions.crManageFlowRuns,
  ]);

  const onHandleCreateFlowRun = () => {
    openModal({ ID: crFlowID });
  };

  const onViewFlowReport = () => {
    history.push(cleanRoom.flowRunList(crId, crFlowID));
  };

  const handleScheduleRuns = () => {
    history.push(cleanRoom.flowScheduleRunList(crId, crFlowID));
  };

  const createFlowRun = {
    label: 'Create Run',
    action: () => onHandleCreateFlowRun(),
  };

  const viewFlowReportRun = {
    label: 'View Run Reports',
    action: () => onViewFlowReport(),
  };

  const scheduleRuns = {
    label: 'Schedule Runs',
    action: () => handleScheduleRuns(),
  };

  if (canCreateRuns && jobStage === FLOW_STATUS.READY.key) {
    itemsMenu.push(createFlowRun);
    itemsMenu.push(scheduleRuns);
  }

  if (canViewReports && runCount > 0) {
    itemsMenu.push(viewFlowReportRun);
  }

  return itemsMenu;
};

export const FlowReportRunButton = ({ flowDetails }) => {
  const history = useHistory();
  const { crId } = useParams();
  const { openModal } = React.useContext(FlowRunContext);
  const itemsMenu = getMenuItems({
    crFlowID: flowDetails.ID,
    runCount: flowDetails.runCount,
    openModal,
    history,
    crId,
    jobStage: flowDetails?.stage,
  });
  const label = getButtonLabel({
    runCount: flowDetails.runCount,
    jobStage: flowDetails?.stage,
  });
  const shouldDisplayMenuButton = flowDetails?.stage === FLOW_STATUS.READY.key
    || flowDetails?.runCount > 0;

  const tooltipContent = getTooltipContent({
    runCount: flowDetails?.runCount,
    jobStage: flowDetails?.stage,
  });


  return (
    <>
      <Tooltip title={tooltipContent}>
        <Box>
          <DropdownMenu
            color='secondary'
            icon={<ArrowDropDownIcon />}
            label={label}
            menuItems={itemsMenu}
            size='small'
            variant='contained'
            disabled={shouldDisplayMenuButton}
          />
        </Box>
      </Tooltip>
    </>
  );
};

export const FlowCardActionButtons = ({ flowDetails }) => (
  <>
    <FlowDatasetsManagement flowDetails={flowDetails} />
    <FlowReportRunButton flowDetails={flowDetails} />
  </>
);
